<template>
	<v-text-field
		ref="textField"
		:value="value"
		:append-icon="appendIcon"
		:autocomplete="autocomplete"
		:autofocus="autofocus"
		:background-color="backgroundColor"
		:clearable="clearable"
		:clear-icon="clearIcon"
		:color="color"
		:dense="dense"
		:disabled="disabled"
		:error-messages="errorMessages"
		:flat="flat"
		:hide-details="hideDetails"
		:label="label"
		:light="light"
		:maxlength="maxlength"
		:outlined="outlined"
		:placeholder="placeholder"
		:prepend-inner-icon="prependInnerIcon"
		:readonly="readonly"
		:rounded="rounded"
		:rules="rules"
		:single-line="singleLine"
		:solo="solo"
		:type="type"
		:solo-inverted="underlined"
		v-bind="forwardedAttrs"
		v-on="forwardedListeners"
		@click:append="$emit('click:append', $event)"
	>
		<template v-slot:prepend-inner>
			<slot name="prepend-inner"></slot>
		</template>
		<template v-slot:append>
			<slot name="append"></slot>
		</template>
		<template v-slot:label>
			<slot name="label"></slot>
		</template>
	</v-text-field>
</template>

<script>
export default {
	name: 'KTextField',
	inheritAttrs: false,
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		value: {
			type: [String, Number],
			default: undefined
		},
		appendIcon: {
			type: String,
			default: undefined
		},
		autocomplete: {
			type: String,
			default: undefined
		},
		autofocus: {
			type: Boolean,
			default: false
		},
		backgroundColor: {
			type: String,
			default: undefined
		},
		clearable: {
			type: Boolean,
			default: false
		},
		clearIcon: {
			type: String,
			default: undefined
		},
		color: {
			type: String,
			default: undefined
		},
		dense: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		errorMessages: {
			type: [String, Array],
			default: undefined
		},
		flat: {
			type: Boolean,
			default: false
		},
		hideDetails: {
			type: [Boolean, String],
			default: undefined
		},
		label: {
			type: String,
			default: undefined
		},
		light: {
			type: Boolean,
			default: false
		},
		maxlength: {
			type: [Number, String],
			default: undefined
		},
		outlined: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: undefined
		},
		prependInnerIcon: {
			type: String,
			default: undefined
		},
		readonly: {
			type: Boolean,
			default: false
		},
		rounded: {
			type: Boolean,
			default: false
		},
		rules: {
			type: Array,
			default: () => []
		},
		singleLine: {
			type: Boolean,
			default: false
		},
		solo: {
			type: Boolean,
			default: false
		},
		type: {
			type: String,
			default: 'text'
		},
		underlined: {
			type: Boolean,
			default: false
		},
		appendAriaLabel: {
			type: String,
			default: undefined
		},
		appendAriaPressed: {
			type: [Boolean, String],
			default: undefined
		},
		prependAriaLabel: {
			type: String,
			default: undefined
		},
		prependDecorative: {
			type: Boolean,
			default: false
		},
		accessibleLabel: {
			type: String,
			default: undefined
		}
	},
	computed: {
		forwardedAttrs() {
			const { 'accessible-label': accessible_label, ...rest } = this.$attrs
			if (this.resolvedAriaLabel && !rest['aria-label'] && !rest['aria-labelledby']) {
				rest['aria-label'] = this.resolvedAriaLabel
			}
			return rest
		},
		forwardedListeners() {
			const { 'click:append': clickAppend, ...rest } = this.$listeners
			return rest
		},
		resolvedAriaLabel() {
			return this.accessibleLabel || this.$attrs['aria-label']
		}
	},
	mounted() {
		this.syncAppendButtonAccessibility()
		this.syncPrependButtonAccessibility()
		this.syncHiddenLegendAccessibility()
		this.syncInputAccessibility()
	},
	updated() {
		this.syncAppendButtonAccessibility()
		this.syncPrependButtonAccessibility()
		this.syncHiddenLegendAccessibility()
		this.syncInputAccessibility()
	},
	methods: {
		focus() {
			this.$refs.textField?.focus?.()
		},
		syncInputAccessibility() {
			const root = this.$refs.textField?.$el
			if (!root || !this.resolvedAriaLabel) return

			const input = root.querySelector('input, textarea')
			if (!input) return

			input.setAttribute('aria-label', this.resolvedAriaLabel)
		},
		syncAppendButtonAccessibility() {
			const root = this.$refs.textField?.$el
			if (!root) return
			const appendButton = root.querySelector('.v-input__append-inner button')
			if (!appendButton) return
			if (this.appendAriaLabel) appendButton.setAttribute('aria-label', this.appendAriaLabel)
			if (this.appendAriaPressed !== undefined) appendButton.setAttribute('aria-pressed', String(this.appendAriaPressed))
		},
		syncPrependButtonAccessibility() {
			const root = this.$refs.textField?.$el
			if (!root) return

			const prependButton = root.querySelector('.v-input__prepend-inner button')
			if (!prependButton) return

			if (this.prependAriaLabel) prependButton.setAttribute('aria-label', this.prependAriaLabel)

			if (this.prependDecorative) {
				// This keeps prepend icon affordances clickable for pointer users while removing
				// the extra keyboard stop and announcement when the input itself already names the action.
				prependButton.setAttribute('tabindex', '-1')
				prependButton.setAttribute('aria-hidden', 'true')
				prependButton.setAttribute('focusable', 'false')
			}
		},
		syncHiddenLegendAccessibility() {
			const root = this.$refs.textField?.$el
			if (!root) return

			const hiddenLegendSpan = root.querySelector('fieldset[aria-hidden="true"] legend span')
			if (!hiddenLegendSpan) return

			// Vuetify uses this span only to size the outlined notch. We keep it in the DOM
			// for layout, but hide it explicitly so scanner tools don't report contrast on it.
			hiddenLegendSpan.setAttribute('aria-hidden', 'true')
			hiddenLegendSpan.style.opacity = '0'
			hiddenLegendSpan.style.color = 'transparent'
			hiddenLegendSpan.style.backgroundColor = 'transparent'
		}
	}
};
</script>

<style lang="scss">
.v-application {
	.v-input.v-text-field.v-input--is-focused.v-text-field--outlined {
		.v-input__slot input:focus,
		.v-input__slot input:focus-visible,
		.v-input__slot textarea:focus,
		.v-input__slot textarea:focus-visible {
			outline: none !important;
			box-shadow: none !important;
		}

		.v-input__slot fieldset {
			border: 2px solid #1565c0 !important;
		}
	}
}
</style>
